/**
 * onboarding.js — First-visit onboarding overlay (4 stages)
 *
 * initOnboarding(sounds) → void
 *
 * Stages: welcome → register → blueprint → early
 * Exposes window.obShowRegister, window.obNext, window.obSkip, window.obFinish
 * so inline onclick= attributes in CONTENT templates work without changes.
 */

import gsap from 'gsap'

export function initOnboarding(sounds) {
    if (localStorage.getItem('ob_done')) return

    const overlay = document.getElementById('ob-overlay')
    const stage   = document.getElementById('ob-stage')

    if (localStorage.getItem('ob_done')) {
        overlay.style.pointerEvents = 'none'
        stage.style.pointerEvents   = 'none'
        return
    }

    overlay.style.display    = 'block'
    overlay.style.background = 'rgba(5,2,0,0.92)'
    stage.style.display      = 'flex'
    stage.style.pointerEvents = 'auto'
    gsap.set(overlay, { opacity: 1 })
    gsap.set(stage,   { opacity: 0, scale: 0.88 })

    const STAGES = ['welcome', 'register', 'blueprint', 'early']
    let cur = 0

    const bpStyle   = `background:rgba(254,217,7,.05);border:1px solid rgba(254,217,7,.2);border-radius:10px;padding:12px 18px;text-align:center;min-width:85px`
    const bpVal     = `font-family:'Courier New',monospace;font-size:1.7rem;color:#fed907;display:block;line-height:1;margin-bottom:4px`
    const bpLbl     = `font-family:'Courier New',monospace;font-size:.45rem;letter-spacing:1.5px;color:rgba(254,217,7,.4);text-transform:uppercase`
    const btnStyle  = `padding:13px 30px;background:transparent;border:1px solid #fed907;color:#fed907;font-family:'Courier New',monospace;font-size:1rem;letter-spacing:4px;text-transform:uppercase;border-radius:8px;position:relative;overflow:hidden;min-width:170px;cursor:pointer;transition:background .3s,box-shadow .3s`
    const shineStyle = `position:absolute;inset:0;background:linear-gradient(90deg,transparent,rgba(254,217,7,.16),transparent);transform:translateX(-100%);animation:ob-shine 2.8s infinite;pointer-events:none`
    const skipStyle = `
    display:block;
    margin-top:16px;
    text-align:center;
    font-family:'Courier New',monospace;
    font-size:.5rem;
    letter-spacing:2px;
    color:rgba(255, 217, 0, 0.82);
    text-transform:uppercase;
    cursor:pointer;
    transition:color .3s;
    padding:8px 16px;
    border:1px solid rgba(254,217,7,.15);
    border-radius:20px;
    background:rgba(254,217,7,.04);
`

    function valueList(items) {
        return `<div style="display:flex;flex-direction:column;gap:8px;max-width:300px;width:100%;margin-bottom:16px">${
            items.map(([icon, text]) => `
                <div style="display:flex;align-items:center;gap:12px;padding:10px 15px;border:1px solid rgba(254,217,7,.2);border-radius:8px;background:rgba(254,217,7,.05)">
                    <span style="font-size:1rem">${icon}</span>
                    <span style="font-family:'Courier New',monospace;font-size:1rem;letter-spacing:1.5px;color:rgba(255,255,255,.62);text-transform:uppercase">${text}</span>
                </div>`
            ).join('')
        }</div>`
    }

    const CONTENT = {
        welcome: `
            <img class="ob-rabbit" src="./rabbit_open_eye_open_mouth.webp" style="width:300px;margin-bottom:40px">
            <div style="font-family:'Courier New',monospace;font-size:1rem;letter-spacing:4px;color:rgba(255, 217, 0, 0.82);text-transform:uppercase;margin-bottom:12px">✦ the oracle ✦</div>
            <h1 style="font-family:'Courier New',monospace;font-size:clamp(1.3rem,4vw,1.9rem);color:#fed907;text-align:center;letter-spacing:4px;text-transform:uppercase;margin-bottom:10px">Welcome, Seeker</h1>
            <p style="color:rgba(255,255,255,.6);text-align:center;line-height:1.75;max-width:360px;margin-bottom:28px">I have been waiting for you.<br>Ask your first question below — I will answer in your language.</p>
            <button onclick="obNext()" style="${btnStyle}">Begin<div style="${shineStyle}"></div></button>
            <div onclick="obSkip()" style="${skipStyle}">Skip intro</div>
        `,
        register: `
            <img class="ob-rabbit" src="./rabbit_open_eye_cloth_mouth.webp" style="width:300px;margin-bottom:40px">
            <div style="font-family:'Courier New',monospace;font-size:1rem;letter-spacing:4px;color:rgba(255, 217, 0, 0.82);text-transform:uppercase;margin-bottom:12px">✦ why to register ✦</div>
            <h2 style="font-family:'Courier New',monospace;font-size:clamp(1.1rem,3vw,1.5rem);color:#fed907;text-align:center;letter-spacing:3px;text-transform:uppercase;line-height:1.3;margin-bottom:16px">The more I know you,<br>the more precise I become.</h2>
            ${valueList([
                ['💬','Your language is remembered'],
                ['📜','Your history is stored'],
                ['✨','Your patterns are revealed'],
                ['🥕','3 questions/day instead of 1'],
            ])}
            <button onclick="obNext()" style="${btnStyle};margin-top:4px">Show my Blueprint<div style="${shineStyle}"></div></button>
            <div onclick="obSkip()" style="${skipStyle}">Skip intro</div>
        `,
        blueprint: `
            <img class="ob-rabbit" src="./rabbit_open_eye_cloth_mouth.webp" style="width:300px;margin-bottom:40px">
            <div style="font-family:'Courier New',monospace;font-size:1rem;letter-spacing:4px;color:rgba(255, 217, 0, 0.82);text-transform:uppercase;margin-bottom:12px">✦ your soul map ✦</div>
            <h2 style="font-family:'Courier New',monospace;font-size:clamp(1.1rem,3vw,1.5rem);color:#fed907;text-align:center;letter-spacing:3px;text-transform:uppercase;margin-bottom:8px">The Blueprint</h2>
            <p style="color:rgba(255,255,255,.55);text-align:center;font-size:1rem;margin-bottom:16px">Three numbers that define your path.</p>
            <div style="display:flex;gap:10px;margin-bottom:16px">
                <div id="ob-bp1" style="opacity:0;transform:scale(.7) translateY(10px);transition:opacity .5s ease,transform .5s ease;${bpStyle}">
                    <span style="${bpVal}">7</span><span style="${bpLbl}">Life Path</span>
                </div>
                <div id="ob-bp2" style="opacity:0;transform:scale(.7) translateY(10px);transition:opacity .5s ease,transform .5s ease;${bpStyle}">
                    <span style="${bpVal}">3</span><span style="${bpLbl}">Expression</span>
                </div>
                <div id="ob-bp3" style="opacity:0;transform:scale(.7) translateY(10px);transition:opacity .5s ease,transform .5s ease;${bpStyle}">
                    <span style="${bpVal}">11</span><span style="${bpLbl}">Soul Urge</span>
                </div>
            </div>
            <div style="background:rgba(254,217,7,.04);border:1px solid rgba(254,217,7,.2);border-radius:8px;padding:12px 16px;max-width:320px;font-size:.82rem;color:rgba(255,255,255,.6);line-height:1.7;margin-bottom:16px;text-align:center">
                Fill your birth details in profile<br>to unlock your real numbers ✦
            </div>
            <button onclick="obNext()" style="${btnStyle}">What's coming next?<div style="${shineStyle}"></div></button>
            <div onclick="obSkip()" style="${skipStyle}">Skip intro</div>
        `,
        early: `
            <img class="ob-rabbit" src="./rabbit_open_eye_open_mouth.webp" style="width:300px;margin-bottom:40px">
            <div style="font-family:'Courier New',monospace;font-size:1rem;letter-spacing:4px;color:rgba(255, 217, 0, 0.82);text-transform:uppercase;margin-bottom:12px">✦ a promise ✦</div>
            <h2 style="font-family:'Courier New',monospace;font-size:clamp(1.1rem,3vw,1.5rem);color:#fed907;text-align:center;letter-spacing:3px;text-transform:uppercase;line-height:1.3;margin-bottom:10px">You arrived before<br>the world found this place.</h2>
            <p style="color:rgba(255,255,255,.55);text-align:center;font-size:1rem;margin-bottom:14px">The Rabbit remembers those who come early.</p>
            <div style="border:1px solid #fed907;border-radius:10px;padding:14px 18px;max-width:320px;width:100%;margin-bottom:16px;background:rgba(254,217,7,.05)">
                <div style="display:flex;align-items:center;gap:10px;padding:6px 0;font-size:1rem;color:rgba(255,255,255,.78)"><span>🔮</span><span><strong style="color:#fed907;font-weight:400">3 months Oracle</strong> — free at full release</span></div>
                <div style="display:flex;align-items:center;gap:10px;padding:6px 0;border-top:1px solid rgba(254,217,7,.2);font-size:1rem;color:rgba(255,255,255,.78)"><span>🎮</span><span><strong style="color:#fed907;font-weight:400">1 Mirror Session</strong> — free transformation</span></div>
            </div>
            <button onclick="obFinish()" style="${btnStyle};background:rgba(254,217,7,.09)">Enter the Rabbit Hole ✦<div style="${shineStyle}"></div></button>
        `
    }

    // ── CSS injection ──────────────────────────────────────────
    const styleEl = document.createElement('style')
    styleEl.textContent = `
    @keyframes ob-shine{0%{transform:translateX(-100%)}55%{transform:translateX(100%)}100%{transform:translateX(100%)}}
    @keyframes ob-levitate{
        0%,100%{ transform:translateY(0px); filter:drop-shadow(0 0 16px rgba(254,217,7,0.4)); }
        50%{ transform:translateY(-12px); filter:drop-shadow(0 0 28px rgba(254,217,7,0.75)); }
    }
    .ob-rabbit{
        animation: ob-levitate 3s ease-in-out infinite;
        will-change: transform, filter;
    }
`
    document.head.appendChild(styleEl)

    // ── Blink ──────────────────────────────────────────────────
    let obBlinkTimer = null

    function obBlink() {
        const rabbit = document.querySelector('.ob-rabbit')
        if (!rabbit) return
        const open   = rabbit.src.includes('open_eye')
            ? rabbit.src
            : rabbit.src.replace('cloth_eye', 'open_eye')
        const closed = open.replace('open_eye', 'cloth_eye')
        rabbit.src = closed
        setTimeout(() => {
            const r = document.querySelector('.ob-rabbit')
            if (r) r.src = open
        }, 150)
    }

    function startObBlink() {
        const delay = 2500 + Math.random() * 2500
        obBlinkTimer = setTimeout(() => {
            obBlink()
            startObBlink()
        }, delay)
    }

    // ── Stage render ───────────────────────────────────────────
    function renderStage(name) {
        stage.innerHTML = CONTENT[name]
        gsap.set(stage, { opacity: 0, scale: 0.88 })
        gsap.to(stage, {
            opacity: 1, scale: 1, duration: .55, ease: 'back.out(1.4)',
            onComplete: () => {
                startObBlink()
                if (name === 'blueprint') {
                    ;['ob-bp1', 'ob-bp2', 'ob-bp3'].forEach((id, i) => {
                        setTimeout(() => {
                            const el = document.getElementById(id)
                            if (el) { el.style.opacity = '1'; el.style.transform = 'scale(1) translateY(0)' }
                        }, 400 + i * 350)
                    })
                }
            }
        })
    }

    // ── Black-hole exit ────────────────────────────────────────
    function blackHoleOut(cb) {
        clearTimeout(obBlinkTimer)
        sounds.woosh.currentTime = 0
        sounds.woosh.play().catch(() => {})
        gsap.to(stage, {
            scale: .04, opacity: 0, duration: .5, ease: 'power3.in',
            onComplete: () => {
                gsap.set(stage, { scale: 1, opacity: 0 })
                stage.innerHTML = ''
                cb()
            }
        })
    }

    // ── window.ob* (called by inline onclick= in CONTENT) ─────
    window.obShowRegister = function() {
        overlay.style.display     = 'block'
        stage.style.display       = 'flex'
        stage.style.pointerEvents = 'auto'
        gsap.set(overlay, { opacity: 1 })
        gsap.set([overlay, stage], { opacity: 0 })
        cur = 1
        gsap.to([overlay, stage], { opacity: 1, duration: .6, onComplete: () => {
            renderStage(STAGES[cur])
        }})
    }

    window.obNext = function() {
        try { sounds.click.currentTime = 0; sounds.click.play() } catch(e) {}
        if (cur === 0) {
            blackHoleOut(() => {
                gsap.to([overlay, stage], { opacity: 0, duration: .5, onComplete: () => {
                    overlay.style.display = 'none'
                    stage.style.display   = 'none'
                    document.getElementById('question')?.focus()
                }})
            })
            return
        }
        cur++
        if (cur >= STAGES.length) { window.obFinish(); return }
        blackHoleOut(() => renderStage(STAGES[cur]))
    }

    window.obSkip = function() {
        try { sounds.click.currentTime = 0; sounds.click.play() } catch(e) {}
        clearTimeout(obBlinkTimer)
        localStorage.setItem('ob_done', '1')
        localStorage.setItem('ob_skipped', '1')
        gsap.to(stage, { opacity: 0, scale: 0.88, duration: .5, onComplete: () => {
            gsap.to(overlay, { opacity: 0, duration: .3, onComplete: () => {
                overlay.style.display     = 'none'
                stage.style.display       = 'none'
                stage.style.pointerEvents = 'none'
                stage.innerHTML           = ''
            }})
        }})
    }

    window.obFinish = function() {
        try { sounds.final.currentTime = 0; sounds.final.play() } catch(e) {}
        clearTimeout(obBlinkTimer)
        localStorage.setItem('ob_done', '1')
        localStorage.setItem('ob_completed', '1')
        gsap.to(stage, { opacity: 0, scale: 0.88, duration: .5, onComplete: () => {
            gsap.to(overlay, { opacity: 0, duration: .3, onComplete: () => {
                overlay.style.display     = 'none'
                stage.style.display       = 'none'
                stage.style.pointerEvents = 'none'
                stage.innerHTML           = ''
            }})
        }})
    }

    // ── Start ──────────────────────────────────────────────────
    renderStage(STAGES[0])
}
